import type { FC } from 'react';
import { Show, ShowFirstMatching } from '@components/Show';
import { Spinner } from '@components/Spinner';
import { Button } from '@components/ui/button';
import type { GetRenewalSummaryResponse } from '@responses/renewal-summary';
import { X } from 'lucide-react';

import { Disclaimer } from '../components/Disclaimer';
import { GeneratingState } from '../components/GeneratingState';
import { StartState } from '../components/StartState';
import { SummaryState } from '../components/SummaryState';
import type { RenewalSummaryPanelState } from '../types';

interface RenewalSummaryPanelProps {
  state: RenewalSummaryPanelState;
  data?: GetRenewalSummaryResponse;
  isStarting: boolean;
  onGenerate: () => void;
  onCopy: () => void;
  onClose: () => void;
}

/**
 * The Summary of Changes drawer: one shell (header, scrolling body, footer
 * with the disclaimer above the actions) around three bodies — start,
 * generating and the saved summary.
 */
export const RenewalSummaryPanel: FC<RenewalSummaryPanelProps> = ({
  state,
  data,
  isStarting,
  onGenerate,
  onCopy,
  onClose,
}) => (
  <div className="flex h-full w-full flex-col overflow-hidden bg-white" data-testid="renewal-summary-drawer">
    <header className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
      <h2 className="text-base font-medium whitespace-nowrap text-gray-900">Summary of Changes</h2>
      <Button variant="ghost" size="icon-sm" className="text-gray-500" onClick={onClose} aria-label="Close">
        <X aria-hidden />
      </Button>
    </header>

    <div className="flex-1 overflow-y-auto">
      <ShowFirstMatching>
        <Show when={state === 'loading'}>
          <div className="flex items-center justify-center py-10">
            <Spinner />
          </div>
        </Show>
        <Show when={state === 'generating' && Boolean(data?.job)}>
          {data?.job && <GeneratingState job={data.job} />}
        </Show>
        <Show when={state === 'summary' && Boolean(data?.summary)}>
          {data?.summary && <SummaryState summary={data.summary} />}
        </Show>
        <Show when={state === 'start'}>
          <StartState />
        </Show>
      </ShowFirstMatching>
    </div>

    <Show when={state !== 'loading'}>
      <footer className="flex flex-col gap-3 border-t border-gray-200 bg-white px-4 py-4">
        <Disclaimer variant={state === 'summary' ? 'summary' : 'pre-summary'} />
        <ShowFirstMatching>
          <Show when={state === 'start'}>
            <Button size="lg" className="w-full" disabled={isStarting} onClick={onGenerate}>
              Generate summary
            </Button>
          </Show>
          <Show when={state === 'summary'}>
            <div className="flex gap-3">
              <Button variant="outline" size="lg" className="flex-1" disabled={isStarting} onClick={onGenerate}>
                Regenerate
              </Button>
              <Button size="lg" className="flex-1" onClick={onCopy}>
                Copy Text
              </Button>
            </div>
          </Show>
        </ShowFirstMatching>
      </footer>
    </Show>
  </div>
);
